/**
 * Service for Fantasy Football API calls
 * Communicates with Fantasy Football REST endpoints
 * Uses httpClient for automatic JWT token injection
 */

import httpClient from './httpClient';
import authService from './authService';
import { API_ENDPOINTS } from '../config/properties';

const fantasyService = {
  /**
   * Helper method to ensure token is present before making requests
   */
  _ensureAuthentication: () => {
    const token = authService.getToken();
    if (!token) {
      console.warn('[fantasyService] No JWT token found in localStorage. User may not be authenticated.');
    } else {
      console.log('[fantasyService] JWT token found, attaching to requests');
    }
    return token;
  },
  // ============ PLAYERS ENDPOINTS ============

  /**
   * Get all fantasy players for a season
   */
  getAllPlayers: async (season = 2024) => {
    try {
      fantasyService._ensureAuthentication();
      const url = `${API_ENDPOINTS.fantasyPlayers}?season=${season}`;
      console.log(`[fantasyService] GET ${url}`);
      const response = await httpClient.get(url);
      const data = await response.json();
      console.log(`[fantasyService] Successfully fetched ${data.length || 0} players`);
      return data;
    } catch (error) {
      console.error('[fantasyService] Error fetching fantasy players:', error);
      throw error;
    }
  },

  /**
   * Get single player by ID
   */
  getPlayerById: async (playerId) => {
    try {
      const url = `${API_ENDPOINTS.fantasyPlayers}/${playerId}`;
      const response = await httpClient.get(url);
      return await response.json();
    } catch (error) {
      console.error('Error fetching player:', error);
      throw error;
    }
  },

  /**
   * Get players filtered by role
   */
  getPlayersByRole: async (role, season = 2024) => {
    try {
      const url = `${API_ENDPOINTS.fantasyPlayers}/by-role/${role}?season=${season}`;
      const response = await httpClient.get(url);
      return await response.json();
    } catch (error) {
      console.error('Error fetching players by role:', error);
      throw error;
    }
  },

  // ============ STATS ENDPOINTS ============

  /**
   * Get gameweek statistics
   */
  getGameweekStats: async (gameweekNumber, season = 2024) => {
    try {
      const url = `${API_ENDPOINTS.fantasyStats}/gameweek/${gameweekNumber}?season=${season}`;
      const response = await httpClient.get(url);
      return await response.json();
    } catch (error) {
      console.error('Error fetching gameweek stats:', error);
      throw error;
    }
  },

  /**
   * Get season statistics
   */
  getSeasonStats: async (season = 2024) => {
    try {
      const url = `${API_ENDPOINTS.fantasyStats}/season/${season}`;
      const response = await httpClient.get(url);
      return await response.json();
    } catch (error) {
      console.error('Error fetching season stats:', error);
      throw error;
    }
  },

  // ============ FIXTURES ENDPOINTS ============

  /**
   * Get all fixtures for a season
   */
  getAllFixtures: async (season = 2024) => {
    try {
      const url = `${API_ENDPOINTS.fantasyFixtures}?season=${season}`;
      const response = await httpClient.get(url);
      return await response.json();
    } catch (error) {
      console.error('Error fetching fixtures:', error);
      throw error;
    }
  },

  /**
   * Get single fixture by ID
   */
  getFixtureById: async (fixtureId) => {
    try {
      const url = `${API_ENDPOINTS.fantasyFixtures}/${fixtureId}`;
      const response = await httpClient.get(url);
      return await response.json();
    } catch (error) {
      console.error('Error fetching fixture:', error);
      throw error;
    }
  },

  /**
   * Get fixtures for specific gameweek
   */
  getFixturesByGameweek: async (gameweekNumber, season = 2024) => {
    try {
      const url = `${API_ENDPOINTS.fantasyFixtures}/gameweek/${gameweekNumber}?season=${season}`;
      const response = await httpClient.get(url);
      return await response.json();
    } catch (error) {
      console.error('Error fetching fixtures by gameweek:', error);
      throw error;
    }
  },

  /**
   * Get fixtures by status (NOT_STARTED, LIVE, FINISHED)
   */
  getFixturesByStatus: async (status, season = 2024) => {
    try {
      const url = `${API_ENDPOINTS.fantasyFixtures}/status/${status}?season=${season}`;
      const response = await httpClient.get(url);
      return await response.json();
    } catch (error) {
      console.error('Error fetching fixtures by status:', error);
      throw error;
    }
  },

  /**
   * Get finished fixtures
   */
  getFinishedFixtures: async (season = 2024) => {
    try {
      const url = `${API_ENDPOINTS.fantasyFixtures}/finished?season=${season}`;
      const response = await httpClient.get(url);
      return await response.json();
    } catch (error) {
      console.error('Error fetching finished fixtures:', error);
      throw error;
    }
  },

  // ============ LEADERBOARD ENDPOINTS ============

  /**
   * Get gameweek leaderboard
   */
  getGameweekLeaderboard: async (gameweekNumber, season = 2024) => {
    try {
      const url = `${API_ENDPOINTS.fantasyLeaderboard}/gameweek/${gameweekNumber}?season=${season}`;
      const response = await httpClient.get(url);
      return await response.json();
    } catch (error) {
      console.error('Error fetching gameweek leaderboard:', error);
      throw error;
    }
  },

  /**
   * Get season leaderboard
   */
  getSeasonLeaderboard: async (season = 2024) => {
    try {
      const url = `${API_ENDPOINTS.fantasyLeaderboard}/season/${season}`;
      const response = await httpClient.get(url);
      return await response.json();
    } catch (error) {
      console.error('Error fetching season leaderboard:', error);
      throw error;
    }
  },
};

export default fantasyService;
